import { Button } from "@/components/ui/button";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { Link } from "react-router-dom";
import type { PageDraftReviewProps } from "@/types/help-hub/page-draft";

/** The owner sees the complete proposed content alongside the current version. */
export const PageDraftReview = ({ state, isLoading, error, busy, onGenerate, onReview,
  savedGuides, hiddenAfterApproval, documentLibraryPath }: PageDraftReviewProps) => {
  if (isLoading) return <GenieLoader size="block" loadingText="Finding your page draft…" />;
  if (error) return <p role="alert" className="text-sm text-red-600">Couldn't load your page draft; refresh to try again.</p>;
  const running = busy || state?.status === "running";
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        {state?.needsApproval
          ? "Review your first guides and questions below before making your page available."
          : "Knowledge changes become suggestions here. Your current page stays as it is until you approve."}
      </p>
      {!state?.draft && savedGuides.length > 0 && (
        <div className="rounded-xl border border-genie-border bg-genie-background p-4">
          <p className="text-sm font-semibold text-gray-900">Your saved work</p>
          <p className="mt-1 text-sm text-gray-600">No draft is waiting for review; your saved guides are still here.</p>
          <ul className="mt-3 space-y-2">
            {savedGuides.map((guide) => (
              <li key={guide.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <Link to={documentLibraryPath} className="inline-flex min-h-[44px] items-center break-words text-genie-primary hover:underline">{guide.name}</Link>
                <span className="text-gray-600">{guide.isPublic ? "Published" : "Saved — hidden from your page"}</span>
              </li>
            ))}
          </ul>
          <a href="#saved-page-guides" className="mt-2 inline-flex min-h-[44px] items-center text-sm font-semibold text-genie-primary hover:underline">View saved guides and publishing settings</a>
        </div>
      )}
      {running && <GenieLoader size="block" loadingText="Preparing your page from your knowledge…" />}
      {(state?.error || state?.stale) && <p role="status" className="text-sm text-gray-600">
        {state.stale ? "Your knowledge has changed since this draft. Prepare a fresh draft before approving." : state.error}
      </p>}
      {state?.draft?.documents.map((document) => (
        <details key={document.documentId} className="rounded-2xl border border-genie-border bg-white p-4" open>
          <summary className="min-h-[44px] cursor-pointer break-words text-sm font-semibold text-gray-900">
            {document.action === "create" ? "New guide" : document.action === "hide" ? "Hide guide — sources removed" : "Updated guide"}: {document.metadata.name}
          </summary>
          {document.previous && document.previous.metadata?.genie_document?.auto !== true && (
            <p className="mb-3 text-sm text-gray-600">You customized this guide; approving will replace its text with the proposed version below.</p>
          )}
          {document.previous && !document.previous.is_public && (
            <p className="mb-3 text-sm text-gray-600">This guide is hidden and will stay hidden after approval.</p>
          )}
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            {document.previous && <div className="min-w-0 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Current</p>
              <p className="break-words text-sm font-semibold text-gray-900">{document.previous.name}</p>
              {document.previous.metadata?.genie_document?.subtitle && <p className="break-words text-sm text-gray-600">{document.previous.metadata.genie_document.subtitle}</p>}
              {(document.previous.metadata?.genie_document?.sections ?? []).map((section, index) => (
                <div key={index} className="break-words text-sm text-gray-700">
                  <p className="font-semibold">{section.heading}</p><p className="whitespace-pre-wrap">{section.body}</p>
                </div>
              ))}
            </div>}
            <div className="min-w-0 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-genie-primary">Proposed</p>
              <p className="break-words text-sm font-semibold text-gray-900">{document.metadata.name}</p>
              {document.metadata.genie_document.subtitle && <p className="break-words text-sm text-gray-600">{document.metadata.genie_document.subtitle}</p>}
              {document.action === "hide" ? <p className="text-sm text-gray-600">Remove this guide from the public page; keep the saved document.</p> :
                document.metadata.genie_document.sections.map((section, index) => (
                  <div key={index} className="break-words text-sm text-gray-700">
                    <p className="font-semibold">{section.heading}</p><p className="whitespace-pre-wrap">{section.body}</p>
                  </div>
                ))}
            </div>
          </div>
        </details>
      ))}
      {state?.draft && hiddenAfterApproval > 0 && (
        <p role="status" className="rounded-xl bg-genie-background p-4 text-sm text-gray-700">
          Saving these changes keeps {hiddenAfterApproval} existing {hiddenAfterApproval === 1 ? "guide hidden" : "guides hidden"} from your page.
          {" "}Your work will be saved; publish it using the switches under <a href="#saved-page-guides" className="inline-flex min-h-[44px] items-center font-semibold text-genie-primary hover:underline">Saved guides</a>.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {state?.draft && <>
          <Button className="min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary text-white disabled:opacity-50"
            disabled={running || state.stale || Boolean(state.error)} onClick={() => onReview("approve")}>
            {hiddenAfterApproval > 0 ? "Save reviewed changes" : state.needsApproval ? "Approve first page content" : "Approve these changes"}
          </Button>
          <Button variant="outline" className="min-h-[44px]" disabled={running} onClick={() => onReview("dismiss")}>Dismiss suggestions</Button>
        </>}
        <Button variant="outline" className="min-h-[44px]" disabled={running} onClick={onGenerate}>
          {state?.draft ? "Prepare a fresh draft" : "Prepare my page"}
        </Button>
      </div>
    </div>
  );
};
